import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  CardContent,
  Textarea,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  Snackbar,
  CssBaseline,
  Switch,
} from '@mui/joy';
import {
  Paper,
  Container,
  Typography as MuiTypography,
} from '@mui/material';
import {
  Save as SaveIcon,
  SmartToy as SmartToyIcon,
} from '@mui/icons-material';
import { 
  getAllSettings,
  updateSettingById,
} from '../../main/API/settings';
import PermissionGate from '../../main/components/PermissionGate';
import { PERMISSIONS } from '../../main/context/AuthContext';
import { usePermissions } from '../../main/hooks/usePermissions';
import { DirectionalMaterialThemeProvider } from '../../main/theme/DirectionalMaterialThemeProvider';
import { translateAxiosErrorMessage } from '../../main/utils/masterApiError';

const CHATBOT_ENABLED_SETTING = 'WHATSAPP_CHATBOT_ENABLED';
const CHATBOT_TRUTHY = new Set(['true', '1', 'yes', 'y', 'on']);

function isChatbotEnabled(value) {
  if (value == null || String(value).trim() === '') {
    return true;
  }
  return CHATBOT_TRUTHY.has(String(value).trim().toLowerCase());
}

export default function WhatsAppChatBotScreen() {
  const { t } = useTranslation('ui');
  const { hasPermission } = usePermissions();
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [allSettings, setAllSettings] = useState([]);
  const [editingValues, setEditingValues] = useState({});
  const [savingSections, setSavingSections] = useState({});
  const [savingChatbotToggle, setSavingChatbotToggle] = useState(false);
  const [initialLoad, setInitialLoad] = useState(false);
  
  // Notification states
  const [successNotification, setSuccessNotification] = useState(false);
  const [failedNotification, setFailedNotification] = useState(false);
  const [responseMessage, setResponseMessage] = useState("");

  const loadAllSettings = useCallback(async () => {
    console.log('WhatsAppChatBotScreen: loadAllSettings called');
    if (!hasPermission(PERMISSIONS.SETTINGS_LIST)) {
      console.warn('User does not have permission to access settings');
      setAllSettings([]);
      return;
    }

    setSettingsLoading(true);
    try {
      const settings = await getAllSettings();
      setAllSettings(Array.isArray(settings) ? settings : []);
    } catch (error) {
      console.error('Failed to load settings:', error);
      setResponseMessage(t('settingsPages.common.loadFailed'));
      setFailedNotification(true);
      setAllSettings([]);
    } finally {
      setSettingsLoading(false);
    }
  }, [hasPermission, t]);

  useEffect(() => {
    if (!initialLoad) {
      console.log('WhatsAppChatBotScreen: Initial load starting');
      setInitialLoad(true);
      loadAllSettings();
    }
  }, [initialLoad, loadAllSettings]);

  // Helper function to format setting names for better readability
  const formatSettingName = (name) => {
    if (!name) return '';
    
    const acronyms = ['API', 'SID', 'URL', 'HMAC', 'SMS', 'ID', 'UID', 'UUID'];
    
    const parts = name.split('_').map(part => {
      const upperPart = part.toUpperCase();
      
      if (acronyms.includes(upperPart)) {
        return upperPart;
      }
      
      return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
    });
    
    return parts.join(' ');
  };

  const chatBotSettingLabel = useCallback(
    (name) =>
      t(`settingsPages.whatsAppChatBot.settingLabels.${name}`, {
        defaultValue: formatSettingName(name),
      }),
    [t],
  );

  // Filter settings for WhatsApp Chat Bot category
  const getWhatsAppChatBotSettings = () => {
    const chatBotSettingNames = [
      'SOON_REPLY_MSG',
      'ERROR_MSG',
      'BRANCH_SAVED_MSG',
      'HOME_LOCATION_SAVED_MSG',
      'REPLY_ANY_MSG',
      'HOME_LOCATION_MSG',
      'CHOOSE_BRANCH_MSG',
      'SORTING_CENTER_MSG',
      'LOCATION_URL',
    ];

    return allSettings.filter(setting => chatBotSettingNames.includes(setting.name));
  };

  // Handle setting value change
  const handleSettingValueChange = (settingId, value) => {
    setEditingValues(prev => ({
      ...prev,
      [settingId]: value
    }));
  };

  // Handle save all settings
  const handleSaveSection = async (settings) => {
    if (!hasPermission(PERMISSIONS.SETTINGS_UPDATE_DETAIL)) {
      setResponseMessage(t('permissions.noPermission', { defaultValue: 'You do not have permission to perform this action.' }));
      setFailedNotification(true);
      return Promise.resolve();
    }
    const sectionKey = 'WhatsAppChatBot-main';
    
    const hasChanges = settings.some(setting => {
      const editedValue = editingValues[setting.id];
      return editedValue !== undefined && editedValue !== setting.value;
    });

    if (!hasChanges) {
      setResponseMessage(t('settingsPages.common.noChangesToSave'));
      setFailedNotification(true);
      return Promise.resolve();
    }

    setSavingSections(prev => ({ ...prev, [sectionKey]: true }));
    
    try {
      const savePromises = settings.map(async (setting) => {
        const newValue = editingValues[setting.id] !== undefined 
          ? editingValues[setting.id] 
          : setting.value;

        if (newValue !== setting.value) {
          const settingData = {
            name: setting.name,
            value: newValue,
            description: setting.description || ''
          };
          return updateSettingById(setting.id, settingData);
        }
        return Promise.resolve();
      });

      await Promise.all(savePromises);
      
      setResponseMessage(t('settingsPages.whatsAppChatBot.updatedSuccess'));
      setSuccessNotification(true);
      
      const updatedValuesMap = {};
      settings.forEach(setting => {
        const editedValue = editingValues[setting.id];
        if (editedValue !== undefined) {
          updatedValuesMap[setting.id] = editedValue;
        }
      });
      
      setAllSettings(prevSettings => {
        return prevSettings.map(setting => {
          if (setting.id in updatedValuesMap) {
            return {
              ...setting,
              value: updatedValuesMap[setting.id]
            };
          }
          return setting;
        });
      });
      
      setEditingValues(prev => {
        const updated = { ...prev };
        settings.forEach(setting => {
          delete updated[setting.id];
        });
        return updated;
      });
      
      return Promise.resolve();
    } catch (error) {
      console.error('Failed to save settings:', error);
      const errorMessage = translateAxiosErrorMessage(error, t) || t('settingsPages.common.saveFailed');
      setResponseMessage(errorMessage);
      setFailedNotification(true);
      return Promise.reject(error);
    } finally {
      setSavingSections(prev => {
        const updated = { ...prev };
        delete updated[sectionKey];
        return updated;
      });
    }
  };

  const chatBotSettings = getWhatsAppChatBotSettings();
  const chatbotEnabledSetting = allSettings.find(
    (setting) => setting && setting.name === CHATBOT_ENABLED_SETTING,
  );
  const chatbotEnabled = chatbotEnabledSetting
    ? isChatbotEnabled(chatbotEnabledSetting.value)
    : true;
  const sectionKey = 'WhatsAppChatBot-main';
  const isSaving = savingSections[sectionKey] || false;
  const hasChanges = chatBotSettings.some(setting => {
    const editedValue = editingValues[setting.id];
    return editedValue !== undefined && editedValue !== setting.value;
  });

  const handleChatbotToggle = async (nextEnabled) => {
    if (!hasPermission(PERMISSIONS.SETTINGS_UPDATE_DETAIL)) {
      setResponseMessage(t('permissions.noPermission', { defaultValue: 'You do not have permission to perform this action.' }));
      setFailedNotification(true);
      return;
    }
    if (!chatbotEnabledSetting) {
      setResponseMessage(t('settingsPages.whatsAppChatBot.missingEnabledSetting'));
      setFailedNotification(true);
      return;
    }

    setSavingChatbotToggle(true);
    try {
      const nextValue = nextEnabled ? 'true' : 'false';
      await updateSettingById(chatbotEnabledSetting.id, {
        name: chatbotEnabledSetting.name,
        value: nextValue,
        description: chatbotEnabledSetting.description || '',
      });
      setAllSettings((prevSettings) =>
        prevSettings.map((setting) =>
          setting.id === chatbotEnabledSetting.id
            ? { ...setting, value: nextValue }
            : setting,
        ),
      );
      setResponseMessage(t('settingsPages.whatsAppChatBot.toggleSaved'));
      setSuccessNotification(true);
    } catch (error) {
      console.error('Failed to save chatbot toggle:', error);
      const errorMessage = translateAxiosErrorMessage(error, t) || t('settingsPages.whatsAppChatBot.toggleSaveFailed');
      setResponseMessage(errorMessage);
      setFailedNotification(true);
    } finally {
      setSavingChatbotToggle(false);
    }
  };

  return (
    <DirectionalMaterialThemeProvider>
      <CssBaseline />
      <Box sx={{ minHeight: "100vh", bgcolor: "#FAFAFA" }}>
        <Box component="main" sx={{ overflow: "auto", pt: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 } }}>
          <Container maxWidth="xl">
            <Box sx={{ mb: 4 }}>
              <MuiTypography variant="h4" sx={{ fontWeight: 700, color: "#1F2937", mb: 1 }}>
                {t('settingsPages.whatsAppChatBot.pageTitle')}
              </MuiTypography>
              <MuiTypography variant="body1" color="#6B7280" sx={{ fontSize: '1.1rem' }}>
                {t('settingsPages.whatsAppChatBot.pageSubtitle')}
              </MuiTypography>
            </Box>

            <PermissionGate permissions={[PERMISSIONS.SETTINGS_LIST]}>
              <Paper 
                elevation={0} 
                sx={{ 
                  mb: 4,
                  borderRadius: 3,
                  border: '1px solid #E5E7EB',
                  background: 'linear-gradient(135deg, #FFFFFF 0%, #F9FAFB 100%)',
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          bgcolor: '#DCFCE7',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 1.5
                        }}
                      >
                        <SmartToyIcon sx={{ color: '#25D366', fontSize: 24 }} />
                      </Box>
                      <MuiTypography variant="h6" sx={{ fontWeight: 600, color: '#1F2937' }}>
                        {t('settingsPages.whatsAppChatBot.cardTitle')}
                      </MuiTypography>
                    </Box>
                    <Divider />
                  </Box>

                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      mb: 3,
                      borderRadius: 2,
                      border: '1px solid #F3F4F6',
                      background: '#FFFFFF',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1F2937', mb: 0.5 }}>
                          {t('settingsPages.whatsAppChatBot.enableToggleLabel')}
                        </Typography>
                        <Typography variant="body2" color="#6B7280" sx={{ fontSize: '0.875rem', mb: 1 }}>
                          {t('settingsPages.whatsAppChatBot.enableToggleHelper')}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: chatbotEnabled ? '#15803D' : '#B45309',
                          }}
                        >
                          {chatbotEnabled
                            ? t('settingsPages.whatsAppChatBot.statusActive')
                            : t('settingsPages.whatsAppChatBot.statusPaused')}
                        </Typography>
                        {!chatbotEnabledSetting && !settingsLoading && (
                          <Typography variant="body2" sx={{ color: 'warning.700', mt: 1 }}>
                            {t('settingsPages.whatsAppChatBot.missingEnabledSetting')}
                          </Typography>
                        )}
                      </Box>
                      {savingChatbotToggle ? (
                        <CircularProgress size="sm" />
                      ) : (
                        <PermissionGate permissions={[PERMISSIONS.SETTINGS_MANAGE]}>
                          <Switch
                            checked={chatbotEnabled}
                            onChange={(e) => handleChatbotToggle(e.target.checked)}
                            disabled={!chatbotEnabledSetting || settingsLoading}
                            color={chatbotEnabled ? 'success' : 'neutral'}
                          />
                        </PermissionGate>
                      )}
                    </Box>
                  </Paper>

                  {settingsLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : chatBotSettings.length === 0 ? (
                    <Alert severity="info">
                      {t('settingsPages.whatsAppChatBot.empty')}
                    </Alert>
                  ) : (
                    <Box>
                      <Grid container spacing={2}>
                        {chatBotSettings.map((setting) => {
                          const currentValue = editingValues[setting.id] !== undefined 
                            ? editingValues[setting.id] 
                            : (setting.value || '');
                          
                          return (
                            <Grid item xs={12} key={setting.id}>
                              <Paper 
                                elevation={0}
                                sx={{
                                  p: 2,
                                  borderRadius: 2,
                                  border: '1px solid #F3F4F6',
                                  background: '#FFFFFF',
                                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                  '&:hover': {
                                    transform: 'translateY(-2px)',
                                    border: '1px solid #E5E7EB',
                                    boxShadow: '0 8px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -5px rgba(0, 0, 0, 0.04)',
                                  }
                                }}
                              >
                                <Box sx={{ mb: 1.5 }}>
                                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1F2937', mb: 0.5 }}>
                                    {chatBotSettingLabel(setting.name)}
                                  </Typography>
                                  {setting.description && (
                                    <Typography variant="body2" color="#6B7280" sx={{ fontSize: '0.875rem' }}>
                                      {setting.description}
                                    </Typography>
                                  )}
                                </Box>
                                
                                <Textarea
                                  fullWidth
                                  value={currentValue}
                                  onChange={(e) => handleSettingValueChange(setting.id, e.target.value)}
                                  placeholder={t('settingsPages.common.enterSettingValue')}
                                  disabled={isSaving}
                                  minRows={3}
                                  sx={{ 
                                    fontFamily: 'monospace',
                                    fontSize: '0.875rem',
                                    '& textarea': {
                                      resize: 'vertical',
                                    },
                                  }}
                                />
                              </Paper>
                            </Grid>
                          );
                        })}
                      </Grid>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                        <PermissionGate permissions={[PERMISSIONS.SETTINGS_MANAGE]}>
                          <Button
                            type="button"
                            variant="contained"
                            size="sm"
                            startDecorator={isSaving ? <CircularProgress sx={{ width: 16, height: 16 }} /> : <SaveIcon />}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              const scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
                              handleSaveSection(chatBotSettings).then(() => {
                                requestAnimationFrame(() => {
                                  window.scrollTo(0, scrollPosition);
                                });
                              });
                            }}
                            disabled={isSaving || !hasChanges}
                            sx={{ minWidth: 120 }}
                          >
                            {isSaving ? t('settingsPages.common.saving') : t('actions.save')}
                          </Button>
                        </PermissionGate>
                      </Box>
                    </Box>
                  )}
                </CardContent>
              </Paper>
            </PermissionGate>

            {/* Success Notification */}
            <Snackbar
              autoHideDuration={3000}
              open={successNotification}
              variant="soft"
              color="success"
              onClose={(event, reason) => {
                if (reason === 'clickaway') {
                  return;
                }
                setSuccessNotification(false);
              }}
            >
              {responseMessage}
            </Snackbar>

            {/* Error Notification */}
            <Snackbar
              autoHideDuration={3000}
              open={failedNotification}
              variant="soft"
              color="danger"
              onClose={(event, reason) => {
                if (reason === 'clickaway') {
                  return;
                }
                setFailedNotification(false);
              }}
            >
              {responseMessage}
            </Snackbar>
          </Container>
        </Box>
      </Box>
    </DirectionalMaterialThemeProvider>
  );
}

